import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './context/ThemeContext';
import './styles/global.css';

import LoginPage        from './pages/LoginPage';
import GuestSubmitPage  from './pages/GuestSubmitPage';
import DashboardPage    from './pages/DashboardPage';
import TicketsPage      from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import NewTicketPage    from './pages/NewTicketPage';
import ProjectsPage     from './pages/ProjectsPage';
import NewProjectPage   from './pages/NewProjectPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import KnowledgebasePage from './pages/KnowledgebasePage';
import KbArticlePage    from './pages/KbArticlePage';
import AdminPage        from './pages/AdminPage';
import ProfilePage      from './pages/ProfilePage';
import PreferencesPage  from './pages/PreferencesPage';
import WorkbookPage     from './pages/WorkbookPage';
import AssetsPage       from './pages/AssetsPage';
import AssetFormPage    from './pages/AssetFormPage';
import AssetDetailPage  from './pages/AssetDetailPage';
import ReportsPage      from './pages/ReportsPage';
import AppShell         from './components/shared/AppShell';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login"  element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/submit" element={<GuestSubmitPage />} />
      <Route path="/"       element={<Navigate to="/dashboard" replace />} />

      {/* Protected routes */}
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/dashboard"            element={<DashboardPage />} />
        <Route path="/tickets"              element={<TicketsPage />} />
        <Route path="/tickets/new"          element={<NewTicketPage />} />
        <Route path="/tickets/:id"          element={<TicketDetailPage />} />
        <Route path="/projects"             element={<ProjectsPage />} />
        <Route path="/projects/new"         element={<NewProjectPage />} />
        <Route path="/projects/:id"         element={<ProjectDetailPage />} />
        <Route path="/knowledgebase"        element={<KnowledgebasePage />} />
        <Route path="/knowledgebase/:slug"  element={<KbArticlePage />} />
        <Route path="/profile"             element={<ProfilePage />} />
        <Route path="/preferences"         element={<PreferencesPage />} />
        <Route path="/workbook"            element={<WorkbookPage />} />
        <Route path="/assets"              element={<AssetsPage />} />
        <Route path="/assets/new"          element={<AssetFormPage />} />
        <Route path="/assets/:id"          element={<AssetDetailPage />} />
        <Route path="/assets/:id/edit"     element={<AssetFormPage />} />
        <Route path="/reports"             element={<ReportsPage />} />
        <Route path="/admin"               element={<RequireAdmin><AdminPage /></RequireAdmin>} />
        <Route path="/admin/:tab"          element={<RequireAdmin><AdminPage /></RequireAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '13.5px',
                background: '#1a1a18',
                color: '#fff',
                borderRadius: '8px',
                padding: '10px 14px',
              },
              success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }}
          />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
